import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Paper, Grid, List, ListItem, Divider, Chip, TextField, Stepper, Step, StepLabel, Checkbox, FormControlLabel, MenuItem, Slider, Box, CircularProgress
} from '@mui/material';
import { AddAPhoto as AddAPhotoIcon, Print as PrintIcon } from '@mui/icons-material';
import { format, parseISO, parse, differenceInMinutes } from 'date-fns';
import { formatPhoneNumber, formatTime12Hour } from '../../utils/formatters';
import { getAvailableVesselsForDate } from '../../firebase/services/vesselService';
import Tesseract from 'tesseract.js';

function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
}

const steps = [
  'Reservation Details',
  'Vessel & Driver Info',
  'Checklist & Boat Condition',
];

const CheckInDialog = ({ open, onClose, reservation }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [checkInNotes, setCheckInNotes] = useState('');
  const [boatInspected, setBoatInspected] = useState(false);
  const [safetyEquipmentVerified, setSafetyEquipmentVerified] = useState(false);
  const [driverLicenseVerified, setDriverLicenseVerified] = useState(false);
  const [waiverSigned, setWaiverSigned] = useState(false);
  const [availableVessels, setAvailableVessels] = useState<any[]>([]);
  const [assignedVesselId, setAssignedVesselId] = useState('');
  const [fuelLevel, setFuelLevel] = useState(4);
  const [hourMeterStart, setHourMeterStart] = useState('');
  const [driverLicensePhoto, setDriverLicensePhoto] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [dob, setDob] = useState('');
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrError, setOcrError] = useState('');

  useEffect(() => {
    if (open && reservation) {
      setActiveStep(0);
      setCheckInNotes('');
      setBoatInspected(false);
      setSafetyEquipmentVerified(false);
      setDriverLicenseVerified(false);
      setWaiverSigned(false);
      setAssignedVesselId('');
      setFuelLevel(4);
      setHourMeterStart('');
      setDriverLicensePhoto(null);
      setAvailableVessels([]);
      // Fetch available vessels
      const fetchVessels = async () => {
        try {
          const vessels = await getAvailableVesselsForDate(
            reservation.vesselType,
            reservation.date,
            reservation.timeSlot,
            reservation.id // Pass the current reservation ID to exclude it
          );
          setAvailableVessels(vessels);
        } catch (err) {
          setAvailableVessels([]);
        }
      };
      fetchVessels();
    }
  }, [open, reservation]);

  if (!reservation) return null;

  // Helper: Check if driver's license photo is attached
  const hasLicensePhoto = !!reservation.driverLicensePhotoUrl;

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Check In: {reservation.customerDetails.firstName} {reservation.customerDetails.lastName}
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Grid container spacing={1}>
          {/* Step 1: Reservation Details & Notes */}
          {activeStep === 0 && (
            <Grid xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Customer Info
              </Typography>
              <Paper sx={{ p: 1.5, mb: 1 }}>
                <Typography variant="body1" component="div">
                  <b>{reservation.customerDetails.firstName} {reservation.customerDetails.lastName}</b>
                </Typography>
                <Typography variant="body2" component="div">
                  {reservation.customerDetails.email}
                </Typography>
                <Typography variant="body2" component="div">
                  {formatPhoneNumber(reservation.customerDetails.phone)}
                </Typography>
              </Paper>
              <Typography variant="subtitle1" gutterBottom>
                Reservation Details
              </Typography>
              <Paper sx={{ p: 1.5, mb: 1 }}>
                <List dense disablePadding>
                  <ListItem>
                    <Grid container spacing={1}>
                      <Grid xs={12} sm={6}>
                        <Typography variant="subtitle2">Date & Time</Typography>
                        <Typography variant="body2">
                          {(() => {
                            try {
                              const dateStr = reservation.date ? format(parseISO(reservation.date), 'EEEE, MMMM d, yyyy') : 'N/A';
                              const startStr = reservation.startTime ? formatTime12Hour(reservation.startTime) : 'N/A';
                              const endStr = reservation.endTime ? formatTime12Hour(reservation.endTime) : 'N/A';
                              return `${dateStr} | ${startStr} - ${endStr}`;
                            } catch (error) {
                              console.error('Error formatting date/time:', error);
                              return 'Invalid date/time';
                            }
                          })()}
                        </Typography>
                      </Grid>
                      <Grid xs={12} sm={6}>
                        <Typography variant="subtitle2">Duration</Typography>
                        <Typography variant="body2">
                          {(() => {
                            const start = reservation.startTime;
                            const end = reservation.endTime;
                            if (start && end) {
                              try {
                                const startDate = parse(start, 'hh:mm a', new Date());
                                const endDate = parse(end, 'hh:mm a', new Date());
                                let diff = differenceInMinutes(endDate, startDate);
                                if (diff < 0) diff += 24 * 60;
                                const hours = Math.floor(diff / 60);
                                const mins = diff % 60;
                                const hourStr = hours === 1 ? '1 Hour' : `${hours} Hours`;
                                const minStr = mins === 0 ? '' : (mins === 1 ? '1 Minute' : `${mins} Minutes`);
                                return minStr ? `${hourStr} ${minStr}` : hourStr;
                              } catch {
                                return 'N/A';
                              }
                            }
                            return 'N/A';
                          })()}
                        </Typography>
                      </Grid>
                    </Grid>
                  </ListItem>
                  <Divider component="li" sx={{ my: 0.5 }} />
                  <ListItem>
                    <Grid container spacing={1}>
                      <Grid xs={12} sm={6}>
                        <Typography variant="subtitle2">Vessel Type Reserved</Typography>
                        <Typography variant="body2">
                          {reservation.vesselType ? toTitleCase(reservation.vesselType) : 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid xs={12} sm={6}>
                        <Typography variant="subtitle2">Damage Waiver</Typography>
                        <Typography variant="body2">
                          {reservation.damageWaiverAccepted === true ? 'Accepted' : reservation.damageWaiverAccepted === false ? 'Declined' : 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </ListItem>
                  <Divider component="li" sx={{ my: 0.5 }} />
                  <ListItem>
                    <Grid container spacing={1}>
                      <Grid xs={12}>
                        <Typography variant="subtitle2">Payment Status</Typography>
                        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                          {reservation.payment && reservation.payment.depositPaid && reservation.payment.remainingAmount > 0 ? (
                            <>
                              <Chip label="Deposit Paid" color="info" size="small" sx={{ mr: 1 }} />
                              <Typography variant="body2">
                                Remaining Due: ${reservation.payment.remainingAmount.toFixed(2)}
                              </Typography>
                            </>
                          ) : reservation.payment && reservation.payment.remainingAmount <= 0 ? (
                            <Chip label="Paid" color="success" size="small" />
                          ) : (
                            <Chip label={reservation.paymentStatus || 'unpaid'} color="warning" size="small" />
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </ListItem>
                  {reservation.payment && reservation.payment.depositPaid && (
                    <ListItem>
                      <Grid container spacing={1}>
                        <Grid xs={4}>
                          <Typography variant="subtitle2">Deposit Amount Paid</Typography>
                          <Typography variant="body2">
                            ${reservation.payment.depositAmount?.toFixed(2) || '0.00'}
                          </Typography>
                        </Grid>
                        <Grid xs={4}>
                          <Typography variant="subtitle2">How Paid</Typography>
                          <Typography variant="body2">
                            {reservation.payment.paymentMethod ? toTitleCase(reservation.payment.paymentMethod) : 'N/A'}
                            {reservation.payment.processor ? ` (${toTitleCase(reservation.payment.processor)})` : ''}
                          </Typography>
                        </Grid>
                        <Grid xs={4}>
                          <Typography variant="subtitle2">Deposit Paid On</Typography>
                          <Typography variant="body2">
                            {(() => {
                              try {
                                return reservation.payment.paymentDate ? format(parseISO(reservation.payment.paymentDate), 'PPpp') : 'N/A';
                              } catch (error) {
                                console.error('Error formatting payment date:', error);
                                return 'Invalid date';
                              }
                            })()}
                          </Typography>
                        </Grid>
                      </Grid>
                    </ListItem>
                  )}
                  {reservation.createdAt && (
                    <ListItem>
                      <Grid container spacing={1}>
                        <Grid xs={12}>
                          <Typography variant="subtitle2">Reservation Made</Typography>
                          <Typography variant="body2">
                            {(() => {
                              try {
                                // Handle various date formats from Firestore
                                let dateObj;
                                
                                if (reservation.createdAt) {
                                  // Check if it's already a Date object
                                  if (reservation.createdAt instanceof Date) {
                                    dateObj = reservation.createdAt;
                                  }
                                  // Check if it's a Firestore Timestamp
                                  else if (reservation.createdAt && typeof reservation.createdAt.toDate === 'function') {
                                    dateObj = reservation.createdAt.toDate();
                                  }
                                  // Check if it's a timestamp number
                                  else if (typeof reservation.createdAt === 'number') {
                                    dateObj = new Date(reservation.createdAt);
                                  }
                                  // Try parsing as ISO string
                                  else if (typeof reservation.createdAt === 'string') {
                                    dateObj = parseISO(reservation.createdAt);
                                  }
                                  // Fallback to new Date()
                                  else {
                                    dateObj = new Date(reservation.createdAt);
                                  }
                                  
                                  // Validate the resulting date
                                  if (isNaN(dateObj.getTime())) {
                                    throw new Error('Invalid date value');
                                  }
                                  
                                  return format(dateObj, 'PPpp');
                                } else {
                                  return 'Not available';
                                }
                              } catch (error) {
                                console.error('Error formatting created date:', error, 'Original value:', reservation.createdAt);
                                return 'Invalid date';
                              }
                            })()}
                          </Typography>
                        </Grid>
                      </Grid>
                    </ListItem>
                  )}
                </List>
              </Paper>
            </Grid>
          )}
          {/* Step 2: Vessel Assignment & Driver Data */}
          {activeStep === 1 && (
            <Grid xs={12}>
              <Paper sx={{ p: 1.5, mb: 1 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Assign Vessel
                </Typography>
                <TextField
                  select
                  label="Select Vessel"
                  value={assignedVesselId}
                  onChange={e => setAssignedVesselId(e.target.value)}
                  fullWidth
                  required
                  sx={{ mb: 2 }}
                  disabled={availableVessels.length === 0}
                  helperText={availableVessels.length === 0 ? 'No available vessels of this type' : ''}
                >
                  {availableVessels.map(vessel => (
                    <MenuItem key={vessel.id} value={vessel.id}>
                      {vessel.name} (#{vessel.registrationNumber})
                    </MenuItem>
                  ))}
                </TextField>
                <Divider sx={{ my: 0.5 }} />
                <Typography variant="subtitle1" gutterBottom>
                  Driver's License
                </Typography>
                {hasLicensePhoto || driverLicensePhoto ? (
                  <Box sx={{ mb: 2 }}>
                    <img
                      src={hasLicensePhoto ? reservation.driverLicensePhotoUrl : driverLicensePhoto!}
                      alt="Driver's License"
                      style={{ maxWidth: 240, borderRadius: 4, border: '1px solid #ccc' }}
                    />
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<AddAPhotoIcon />}
                    component="label"
                    sx={{ mb: 2 }}
                  >
                    Take Photo
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      hidden
                      onChange={async e => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setOcrError('');
                          setOcrLoading(true);
                          const reader = new FileReader();
                          reader.onload = async (ev) => {
                            const imgData = ev.target?.result as string;
                            setDriverLicensePhoto(imgData);
                            try {
                              const { data } = await Tesseract.recognize(imgData, 'eng', {
                                logger: m => {
                                  // Optionally, set progress here
                                }
                              });
                              // Try to extract DOB (look for MM/DD/YYYY or similar)
                              const dobMatch = data.text.match(/(\d{2}[\/\-]\d{2}[\/\-]\d{4})/);
                              if (dobMatch) {
                                setDob(dobMatch[1]);
                              } else {
                                setDob('');
                                setOcrError('Could not extract date of birth from license.');
                              }
                            } catch (err) {
                              setOcrError('OCR failed. Please try again.');
                            } finally {
                              setOcrLoading(false);
                            }
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </Button>
                )}
                {ocrLoading && <Typography variant="body2" color="primary">Extracting data from license...</Typography>}
                {ocrError && <Typography variant="body2" color="error">{ocrError}</Typography>}
                <TextField
                  label="Date of Birth"
                  value={dob}
                  onChange={e => setDob(e.target.value)}
                  fullWidth
                  sx={{ mt: 2 }}
                  placeholder="MM/DD/YYYY"
                />
                {/* Optionally add manual entry fields for license number, expiration, etc. */}
              </Paper>
            </Grid>
          )}
          {/* Step 3: Checklist, Boat Photo, Fuel, Hour Meter */}
          {activeStep === 2 && (
            <Grid xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Check-In Checklist
              </Typography>
              <Paper sx={{ p: 1.5, mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={boatInspected}
                      onChange={(e) => setBoatInspected(e.target.checked)}
                      color="primary"
                      disabled={loading}
                    />
                  }
                  label="Boat inspected before departure"
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                  Check for any pre-existing damage and document condition
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={safetyEquipmentVerified}
                      onChange={(e) => setSafetyEquipmentVerified(e.target.checked)}
                      color="primary"
                      disabled={loading}
                    />
                  }
                  label="Safety equipment verified"
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                  Life jackets, fire extinguisher, horn, etc.
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={driverLicenseVerified}
                      onChange={(e) => setDriverLicenseVerified(e.target.checked)}
                      color="primary"
                      disabled={loading}
                    />
                  }
                  label="Driver's license verified"
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                  Confirm customer has valid driver's license
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={waiverSigned}
                      onChange={(e) => setWaiverSigned(e.target.checked)}
                      color="primary"
                      disabled={loading}
                    />
                  }
                  label="Liability waiver signed"
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                  Ensure customer has signed liability waiver
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddAPhotoIcon />}
                  fullWidth
                  sx={{ mt: 2 }}
                  disabled={loading}
                >
                  Take Boat Photo
                </Button>
                <Divider sx={{ my: 0.5 }} />
                <Typography variant="subtitle1" gutterBottom>
                  Starting Fuel Level
                </Typography>
                <Slider
                  value={fuelLevel}
                  onChange={(_, val) => setFuelLevel(val as number)}
                  min={0}
                  max={4}
                  step={1}
                  marks={[
                    { value: 0, label: 'Empty' },
                    { value: 1, label: '1/4' },
                    { value: 2, label: '1/2' },
                    { value: 3, label: '3/4' },
                    { value: 4, label: 'Full' },
                  ]}
                  sx={{ mb: 2 }}
                />
                <Typography variant="subtitle1" gutterBottom>
                  Hour Meter Start
                </Typography>
                <TextField
                  type="number"
                  label="Hour Meter Start"
                  value={hourMeterStart}
                  onChange={e => setHourMeterStart(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                  inputProps={{ min: 0 }}
                />
                <Divider sx={{ my: 0.5 }} />
                <Typography variant="subtitle1" gutterBottom>
                  Check-In Notes
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  placeholder="Add any additional notes about vessel condition, customer requests, etc."
                  value={checkInNotes}
                  onChange={(e) => setCheckInNotes(e.target.value)}
                  disabled={loading}
                />
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button
          onClick={activeStep === 0 ? onClose : handleBack}
          color="inherit"
          disabled={loading}
        >
          {activeStep === 0 ? 'Cancel' : 'Back'}
        </Button>
        <Box>
          {activeStep < 2 ? (
            <Button
              onClick={handleNext}
              variant="contained"
              color="primary"
              disabled={loading || (activeStep === 1 && !assignedVesselId)}
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={onClose}
              variant="contained"
              color="primary"
              disabled={loading || !boatInspected || !safetyEquipmentVerified || !driverLicenseVerified || !waiverSigned || !assignedVesselId}
              startIcon={loading ? <CircularProgress size={24} /> : null}
            >
              {loading ? 'Processing...' : 'Complete Check-In'}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CheckInDialog; 